import { env } from '@core/config/env';
import { getGameId, getStoredToken } from '@core/utils/auth';
import { handleApiResponse } from '@core/index';
import {
  OfferConfigResponse,
  CreateOfferPayload,
  BulkOfferPayload,
  OfferResponse,
  PresignedUrlResponse,
  OfferListResponse,
  OfferListFilters,
  OfferScheduleDTO,
  OfferPromotionDTO,
  Status,
  OfferSchedule,
} from '../types';
import type { ApiResponse } from '@shared/interface';

const API_BASE_URL = env.apiBaseUrl;

/**
 * Upload image directly (binary) and receive filename and URL
 */
export const uploadOfferImage = async (
  file: File,
  offerId?: number | string,
  name?: string,
): Promise<ApiResponse<{ filename: string; url: string }>> => {
  const formData = new FormData();
  formData.append('file', file);
  if (offerId !== undefined && offerId !== null) {
    formData.append('offer_id', String(offerId));
  }
  if (name) {
    formData.append('name', name);
  }

  const response = await fetch(`${API_BASE_URL}/offer/upload-image`, {
    method: 'POST',
    headers: getAuthHeaders(true),
    body: formData,
  });
  return handleApiResponse<ApiResponse<{ filename: string; url: string }>>(response, 'Failed to upload image');
};
const getAuthHeaders = (isFormData = false) => {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'ngrok-skip-browser-warning': '69420',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
  if (!isFormData) headers['Content-Type'] = 'application/json';
  return headers;
};

/**
 * Field Mapping APIs
 */
export interface MappingVersion {
  version_id: number;
  mapping_id: number;
  version: string;
  value: Record<string, any>;
  webhook_url: string;
  prod_webhook_url: string;
  module_type: string;
  game_id: number;
  created_at: string;
  created_by_name: string;
}

export type MappingApiResponse = ApiResponse<{
  fields: string[];
  response: Record<string, any>;
  webhookUrl: string;
  prodWebhookUrl: string;
  currentVersion?: string;
  versions?: MappingVersion[];
}>;

export const getFieldMapping = async (): Promise<MappingApiResponse> => {
  const gameId = getGameId();
  const response = await fetch(`${API_BASE_URL}/mapping/field/${gameId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<MappingApiResponse>(response, 'Failed to fetch field mapping');
};

export const saveFieldMapping = async (payload: Record<string, any>): Promise<{ success: boolean }> => {
  const gameId = getGameId();
  const body = { game_id: gameId, ...payload };
  const response = await fetch(`${API_BASE_URL}/mapping`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return handleApiResponse<{ success: boolean }>(response, 'Failed to save field mapping');
};

/**
 * Fetch offer configuration
 */
export const getOfferConfig = async (): Promise<OfferConfigResponse> => {
  const gameId = getGameId();
  const response = await fetch(`${API_BASE_URL}/offer/offer-config/${gameId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<OfferConfigResponse>(response, 'Failed to fetch offer config');
};

/**
 * Create/Update Offer Schedule
 */
export const postOfferSchedule = async (payload: OfferScheduleDTO): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/offer/schedule`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse<{ success: boolean }>(response, 'Failed to save schedule');
};

/**
 * Create/Update Offer Promotion
 */
export const postOfferPromotion = async (payload: OfferPromotionDTO): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/offer/promotion`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse<{ success: boolean }>(response, 'Failed to save promotion');
};

/**
 * Bulk create offers
 */
export const bulkCreateOffer = async (payload: Omit<BulkOfferPayload, 'game_id'>): Promise<{ message: string }> => {
  const gameId = getGameId();
  const offerData = { ...payload, game_id: gameId };

  const response = await fetch(`${API_BASE_URL}/offer/bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(offerData),
  });
  return handleApiResponse<{ message: string }>(response, 'Failed to create bulk offers');
};

/**
 * Create a new offer
 */
export const createOffer = async (payload: Omit<CreateOfferPayload, 'game_id'> | FormData): Promise<OfferResponse> => {
  const gameId = getGameId();
  const isFormData = payload instanceof FormData;

  if (isFormData) payload.append('game_id', String(gameId));
  const body = isFormData ? payload : JSON.stringify({ ...payload, game_id: gameId });

  const response = await fetch(`${API_BASE_URL}/offer`, { method: 'POST', headers: getAuthHeaders(isFormData), body });
  return handleApiResponse<OfferResponse>(response, 'Failed to create offer');
};

/**
 * Update an existing offer
 */
export const updateOffer = async (
  id: number,
  payload: Omit<CreateOfferPayload, 'game_id'> | FormData,
): Promise<OfferResponse> => {
  const gameId = getGameId();
  const isFormData = payload instanceof FormData;

  if (isFormData) payload.append('game_id', String(gameId));
  const body = isFormData ? payload : JSON.stringify({ ...payload, game_id: gameId });

  const response = await fetch(`${API_BASE_URL}/offer/update/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(isFormData),
    body,
  });
  return handleApiResponse<OfferResponse>(response, 'Failed to update offer');
};

/**
 * Get offer by ID
 */
export const getOffer = async (id: number): Promise<OfferResponse> => {
  const response = await fetch(`${API_BASE_URL}/offer/${id}`, { method: 'GET', headers: getAuthHeaders() });
  return handleApiResponse<OfferResponse>(response, 'Failed to fetch offer');
};

/**
 * Get offers list with pagination
 */
export const getOffers = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<OfferListResponse> => {
  const gameId = getGameId();
  const searchParams = new URLSearchParams({
    game_id: gameId.toString(),
    ...(params?.page && { page: params.page.toString() }),
    ...(params?.limit && { limit: params.limit.toString() }),
    ...(params?.search && { search: params.search }),
  });
  const response = await fetch(`${API_BASE_URL}/offer?${searchParams}`, { method: 'GET', headers: getAuthHeaders() });
  return handleApiResponse<OfferListResponse>(response, 'Failed to fetch offers');
};

/**
 * Get offers list with advanced filtering
 */
export const getOffersList = async (filters: OfferListFilters): Promise<OfferListResponse> => {
  const gameId = getGameId();
  const searchParams = new URLSearchParams();

  // Add game_id
  searchParams.append('game_id', gameId.toString());

  // Add other filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const response = await fetch(`${API_BASE_URL}/offer?${searchParams}`, { method: 'GET', headers: getAuthHeaders() });
  return handleApiResponse<OfferListResponse>(response, 'Failed to fetch offers');
};

/**
 * Presigned URL & S3 Upload
 */
export const getPresignedUrl = async (fileName: string, fileType: string): Promise<PresignedUrlResponse> => {
  const response = await fetch(`${API_BASE_URL}/upload/presigned-url`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fileName, fileType }),
  });
  return handleApiResponse<PresignedUrlResponse>(response, 'Failed to get presigned URL');
};

/**
 * Upload file to S3 using presigned URL
 */
export const uploadFileToS3 = async (presignedData: PresignedUrlResponse, file: File): Promise<string> => {
  const formData = new FormData();
  Object.entries(presignedData.fields).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);

  const response = await fetch(presignedData.url, { method: 'POST', body: formData });
  if (!response.ok) throw new Error(`Failed to upload file: ${response.statusText}`);
  return `${presignedData.url}/${presignedData.fields.key}`;
};

/**
 * Update offer status (Active/Inactive)
 */
export const updateOfferStatus = async (id: number, status: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/offer/status/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });
  return handleApiResponse<{ message: string }>(response, 'Failed to update offer status');
};

/**
 * Get offer schedule logs by offer ID
 */
export const getOfferScheduleLogs = async (offerId: number | string): Promise<ApiResponse<OfferSchedule[]>> => {
  const response = await fetch(`${API_BASE_URL}/offer/logs/${offerId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<ApiResponse<OfferSchedule[]>>(response, 'Failed to fetch schedule logs');
};

/**
 * Sync log interface
 */
export interface SyncLog {
  sync_log_id: number;
  created_at: string;
  sync_status: 'success' | 'error';
  response_message: string | null;
  error_details: string | null;
  sync_on: 'staging' | 'prod';
  request_payload: { [key: string]: any };
}

/**
 * Get offer sync logs by offer ID
 */
export const getOfferSyncLogs = async (offerId: number | string): Promise<ApiResponse<SyncLog[]>> => {
  const response = await fetch(`${API_BASE_URL}/offer/synclog/${offerId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<ApiResponse<SyncLog[]>>(response, 'Failed to fetch sync logs');
};

/**
 * Offer template interface
 */
export interface OfferTemplate {
  offer_template_id: string;
  game_id: number;
  template_name: string;
  template_data: any;
  status: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Get offer templates
 */
export const getOfferTemplates = async (): Promise<ApiResponse<OfferTemplate[]>> => {
  const gameId = getGameId();
  const response = await fetch(`${API_BASE_URL}/offer/templates?game_id=${gameId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<ApiResponse<OfferTemplate[]>>(response, 'Failed to fetch offer templates');
};

/**
 * Create offer template payload
 */
export interface CreateTemplatePayload {
  template_name: string;
  template_data: any;
}

/**
 * Create offer template
 */
export const createOfferTemplate = async (payload: CreateTemplatePayload): Promise<ApiResponse<OfferTemplate>> => {
  const gameId = getGameId();
  const response = await fetch(`${API_BASE_URL}/offer/template`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      game_id: gameId,
      ...payload,
    }),
  });
  return handleApiResponse<ApiResponse<OfferTemplate>>(response, 'Failed to create offer template');
};

/**
 * Download offers as Excel file
 */
export const downloadOffersExcel = async (filters: OfferListFilters): Promise<void> => {
  const gameId = getGameId();
  const searchParams = new URLSearchParams();

  // game_id is required
  searchParams.append('game_id', gameId.toString());

  // Add optional filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && key !== 'page' && key !== 'limit') {
      searchParams.append(key, String(value));
    }
  });

  const response = await fetch(`${API_BASE_URL}/offer/excel/download?${searchParams}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to download Excel file');
  }

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('content-disposition');
  let filename = 'offers-export.xlsx';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename=(.+)/);
    if (match) {
      filename = match[1].replace(/"/g, '');
    }
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  globalThis.URL.revokeObjectURL(url);
};
